/**
 * Weights step of the cycle configure wizard.
 *
 * A weight is a track criterion's share of the track's balance
 * (`cycle_attribute_configs.weight`, migration 1047; canon 02 §2A: criteria and
 * weights live in the Criteria stage). A cycle has no criteria of its own, so this
 * step sets one balance and saves it to each track ticked under "Writes to",
 * through `saveTrackCriteriaWeights` — the save the track's Criteria & Scales tab
 * makes. A track that holds only some of the criteria takes the same balance
 * scaled to 100 over the ones it holds. Saving "no weights" clears them: sessions
 * on those tracks then start from an even split.
 *
 * A track whose criteria are locked holds its weights too, so it is listed as
 * locked and left alone; so is a track whose criteria the person may not change
 * (the right that adds and removes a criterion, migration 1006). The database
 * refuses both anyway (1047).
 */
import { useEffect, useMemo, useState } from 'react';
import { Loader2, Scale } from 'lucide-react';
import { useAttributesStore } from '../../../stores/prioritization/attributesStore';
import {
  useAttributeGovernanceStore,
  criteriaRowsForTrack,
  type CycleAttributeConfig,
} from '../../../stores/prioritization/attributeGovernanceStore';
import { useCycleTracksAuthority } from '../../../hooks/prioritization/useTrackAuthority';
import { useChosenTracks, useCycleOpenTracks } from '../../../hooks/prioritization/useCycleOpenTracks';
import { wizardTrackTarget } from '../../../lib/cycleWizardTargets';
import {
  checkTrackWeights,
  formatWeight,
  weightsForTrack,
  type TrackWeights,
} from '../../../lib/trackCriteriaWeights';
import { CycleTrackTargets, TrackWriteReport, type TrackWriteFailure } from './CycleTrackTargets';

export interface CycleWeightsPanelProps {
  cycleId: string;
}

export function CycleWeightsPanel({ cycleId }: CycleWeightsPanelProps) {
  const attributes = useAttributesStore((s) => s.attributes);
  const fetchAttributes = useAttributesStore((s) => s.fetchFromSupabase);
  const cycleConfigs = useAttributeGovernanceStore((s) => s.cycleConfigs);
  const fetchCycleConfigs = useAttributeGovernanceStore((s) => s.fetchCycleConfigs);
  const saveTrackCriteriaWeights = useAttributeGovernanceStore((s) => s.saveTrackCriteriaWeights);

  useEffect(() => {
    void fetchAttributes();
    void fetchCycleConfigs(cycleId);
  }, [cycleId, fetchAttributes, fetchCycleConfigs]);

  const { tracks } = useCycleOpenTracks(cycleId);
  const authorityOf = useCycleTracksAuthority(cycleId);
  const targets = tracks.map((t) => wizardTrackTarget('weights', t, authorityOf(t).criteriaGate));
  const { chosenIds, toggle: toggleTrack } = useChosenTracks(targets);

  const trackName = (id: string) => tracks.find((t) => t.id === id)?.name ?? 'A track';
  const labelOf = (key: string) => attributes.find((a) => a.key === key)?.label ?? key;

  // Each ticked track's criteria rows, and the criteria they use between them, in track order.
  const rowsByTrack = useMemo(() => {
    const map = new Map<string, CycleAttributeConfig[]>();
    for (const t of tracks) if (chosenIds.includes(t.id)) map.set(t.id, criteriaRowsForTrack(cycleConfigs, t));
    return map;
  }, [tracks, chosenIds, cycleConfigs]);
  const keys = useMemo(() => {
    const seen: string[] = [];
    for (const rows of rowsByTrack.values()) for (const r of rows) if (!seen.includes(r.attributeKey)) seen.push(r.attributeKey);
    return seen;
  }, [rowsByTrack]);

  // What the tracks hold now: a criterion's weight where every ticked track that uses it agrees.
  const current = useMemo(() => {
    const out: Record<string, string> = {};
    for (const key of keys) {
      const values = [...rowsByTrack.values()].flatMap((rows) => rows.filter((r) => r.attributeKey === key).map((r) => r.weight ?? null));
      out[key] = values.length > 0 && values.every((v) => v === values[0]) && values[0] != null ? formatWeight(values[0]) : '';
    }
    return out;
  }, [keys, rowsByTrack]);

  const [typed, setTyped] = useState<Record<string, string> | null>(null);
  const shown = typed ?? current;
  const weights: TrackWeights = Object.fromEntries(keys.map((k) => [k, (shown[k] ?? '').trim() === '' ? null : Number(shown[k])]));
  const check = checkTrackWeights(weights);

  const [saving, setSaving] = useState(false);
  const [report, setReport] = useState<{ saved: number; failures: TrackWriteFailure[] } | null>(null);

  const tracksWithCriteria = chosenIds.filter((id) => (rowsByTrack.get(id)?.length ?? 0) > 0);
  const partialTracks = tracksWithCriteria.filter((id) => (rowsByTrack.get(id)?.length ?? 0) < keys.length);

  const saveAll = async (balance: TrackWeights | null) => {
    setSaving(true);
    const failures: TrackWriteFailure[] = [];
    let saved = 0;
    try {
      for (const trackId of tracksWithCriteria) {
        const trackKeys = (rowsByTrack.get(trackId) ?? []).map((r) => r.attributeKey);
        const next = balance
          ? weightsForTrack(balance, trackKeys)
          : Object.fromEntries(trackKeys.map((k) => [k, null]));
        const { error } = await saveTrackCriteriaWeights(trackId, next);
        if (error) failures.push({ trackName: trackName(trackId), what: 'the weights', error });
        else saved += 1;
      }
      await fetchCycleConfigs(cycleId);
      setTyped(null);
      setReport({ saved, failures });
    } finally {
      setSaving(false);
    }
  };

  const n = tracksWithCriteria.length;
  const tracksWord = `${n} track${n === 1 ? '' : 's'}`;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-[var(--text-pri)] flex items-center gap-2">
          <Scale className="h-5 w-5 text-[var(--text-ter)]" />
          Weights
        </h3>
        <p className="text-sm text-[var(--text-ter)] mt-1">
          How much each criterion counts. Sessions on these tracks start from these weights; with no
          weights they start from an even split. The weights are saved on each track below, as on a
          track&apos;s Criteria tab.
        </p>
      </div>

      <CycleTrackTargets targets={targets} chosenIds={chosenIds} onToggle={toggleTrack} />

      {report && <TrackWriteReport saved={report.saved} failures={report.failures} />}

      {keys.length === 0 ? (
        <p className="text-sm text-[var(--text-sec)]">
          {chosenIds.length === 0 ? 'No track here can take weights.' : 'These tracks have no criteria yet. Pick criteria in the Criteria step.'}
        </p>
      ) : (
        <>
          <ul className="space-y-2">
            {keys.map((key) => (
              <li key={key} className="flex items-center justify-between gap-3 border border-[var(--rule)] bg-[var(--bg-raised)] px-4 py-2">
                <span className="text-[var(--text-pri)]">{labelOf(key)}</span>
                <label className="flex items-center gap-1.5 text-sm text-[var(--text-sec)]">
                  <input
                    aria-label={`Weight for ${labelOf(key)}`}
                    type="number"
                    min={0}
                    max={100}
                    step={1}
                    inputMode="decimal"
                    value={shown[key] ?? ''}
                    onChange={(e) => setTyped({ ...shown, [key]: e.target.value })}
                    className="w-20 px-2 py-1 border border-[var(--rule)] bg-[var(--bg)] text-right"
                  />
                  %
                </label>
              </li>
            ))}
          </ul>

          {partialTracks.length > 0 && (
            <p className="text-xs text-[var(--text-ter)]">
              {partialTracks.map(trackName).join(', ')} {partialTracks.length === 1 ? 'uses' : 'use'} only some of
              these criteria; {partialTracks.length === 1 ? 'it takes' : 'they take'} the same balance, scaled to 100
              over the criteria {partialTracks.length === 1 ? 'it holds' : 'each holds'}.
            </p>
          )}

          <div className="flex flex-wrap items-center justify-between gap-3 pt-4 border-t border-[var(--rule)]">
            <span className={`text-sm ${check.ok ? 'text-[var(--text-sec)]' : 'text-[var(--mustard)]'}`} role="status">
              {check.noneSet ? 'No weights: an even split' : `Total ${formatWeight(check.total)} of 100`}
              {!check.ok && check.error ? ` — ${check.error}` : ''}
            </span>
            {n > 0 && (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => void saveAll(null)}
                  className="px-3 py-1.5 text-sm text-[var(--text-sec)] hover:bg-[var(--hover-tint)] disabled:opacity-50"
                >
                  No weights on {tracksWord}
                </button>
                <button
                  type="button"
                  disabled={saving || check.noneSet || !check.ok}
                  onClick={() => void saveAll(weights)}
                  className="btn btn-primary flex items-center gap-2 disabled:opacity-50"
                >
                  {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                  Save weights to {tracksWord}
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default CycleWeightsPanel;
